import { Alert, Box, Card, CardContent, Grid, List, Theme, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useFlags, useAllowedNavRoutes } from '../../hooks';
import { cloneElement, Fragment, useContext, useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../../context';
import { getNews, getVendorCertificatesExpiring, getAnyVendorConfigured } from '../../fetch';
import {
  hasNavAccess,
  vendorLinks,
  hasNavActiveFlags,
  filterNavByAllowedRoutes
} from '../../helpers';
import { VendorPage } from './VendorPage';
import { INews } from '../../models';
import { NewsCard } from '../news/news-card';
import { Loader } from '../../components';

export const VendorPortal = () => {
  const classes = useStyles();
  const [isLoading, setLoading] = useState(false);
  const userContext = useContext(UserContext);
  const { user } = useContext(UserContext);
  const [expiredContractMessages, showExpiredContractMessages] = useState<string[]>([]);
  const featureFlags = useFlags();
  const [news, setNews] = useState<INews[]>([]);
  const [isAnyVendorConfigured, setAnyVendorConfigured] = useState<boolean>(false);
  const { availableLinks } = useAllowedNavRoutes(featureFlags, user);

  const filteredLinks = useMemo(() => {
    let links = vendorLinks;
    if (featureFlags?.authorizeUserNavigationAccess && availableLinks?.size > 0) {
      links = filterNavByAllowedRoutes(links, new Set(availableLinks));
    } else {
      links = links.filter(link => hasNavAccess(link?.roles, userContext));
    }

    return links;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorLinks, availableLinks, featureFlags, user]);

  // not bothering to catch any errors here since this will only show if there value in the response
  // and the user doesn't need to see success or error
  const fetchVendorCertificatesExpiring = async () => {
    // for testing use id 47
    // const res = await getVendorCertificatesExpiring([47]);
    const res = await getVendorCertificatesExpiring(
      user?.externalUser?.partners?.map(part => part.companyId)
    );
    showExpiredContractMessages(res);
  };

  const fetchNews = async () => {
    try {
      setLoading(true);
      const newsData = await getNews({
        onlyActive: true,
        sortBy: 'CreatedDate',
        sortDirection: 'Desc'
      });
      setNews(newsData.records);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchAnyVendorConfigured = async () => {
    try {
      if (user?.userRoles?.includes('VendorUserManagement')) {
        const res = await getAnyVendorConfigured();
        setAnyVendorConfigured(res);
      }
    } catch (error) {}
  };

  useEffect(() => {
    fetchVendorCertificatesExpiring();
    fetchAnyVendorConfigured();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    fetchNews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showNews = news?.length > 0;
  return (
    <VendorPage>
      {isLoading && <Loader type="inline" position="centered" />}
      {!isLoading && (
        <>
          {expiredContractMessages && expiredContractMessages.length > 0 && (
            <Alert severity="warning">
              <Box component="ul" padding="0 1.5rem" margin="0">
                {expiredContractMessages.map((mes, index) => {
                  return (
                    <li key={`${index}`}>
                      {mes} is going to expire soon, please provide the latest copy to Stone Martin
                      Builders.
                    </li>
                  );
                })}
              </Box>
            </Alert>
          )}
          <Grid spacing={2} container>
            <Grid item xs={12} lg={showNews ? 8 : 12} xl={showNews ? 9 : 12}>
              <Grid spacing={2} container alignItems="center">
                {filteredLinks
                  .filter(link => hasNavAccess(link?.roles, userContext))
                  .filter(item => !!hasNavActiveFlags(item, featureFlags))
                  .filter(item => !(item.name === 'User Management' && !isAnyVendorConfigured))
                  .map((link, i) => {
                    return (
                      <Fragment key={`${i}`}>
                        <Grid xs={12} sm={6} md={4} item>
                          <Link to={link.url} className={classes.link}>
                            <Card className={classes.cardRoot}>
                              <CardContent className={classes.content}>
                                {cloneElement(link.icon, { fontSize: 'large', color: 'primary' })}
                                <Typography>{link.name}</Typography>
                              </CardContent>
                            </Card>
                          </Link>
                        </Grid>
                      </Fragment>
                    );
                  })}
              </Grid>
            </Grid>

            {showNews && (
              <Grid item xs={12} lg={4} xl={3} flexDirection="column" display="flex">
                <Box padding="1rem" border="1px solid #e1e1e1" borderRadius="4px">
                  <List
                    aria-labelledby="list-header"
                    subheader={
                      <Typography
                        color="primary"
                        variant="h5"
                        id="list-header"
                        sx={{ marginBottom: theme => theme.spacing(0.5) }}
                      >
                        News
                      </Typography>
                    }
                  >
                    {news.slice(0, 3).map((newsItem: INews, index) => {
                      return (
                        <Box mb={1} key={`${index}`}>
                          <NewsCard newsItem={newsItem} />
                        </Box>
                      );
                    })}
                    {news?.length > 3 && <Link to="/news">All News</Link>}
                  </List>
                </Box>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </VendorPage>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  pageTitleWrapper: {
    justifyContent: 'space-around',
    marginBottom: theme.spacing(0.5),
    '@media (min-width: 768px)': {
      flex: 1
    }
  },
  wrapper: {
    marginTop: '2rem'
  },
  pageWrapper: {
    marginTop: 0
  },
  tabBar: {
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  },
  link: {
    textDecoration: 'none'
  },
  infoWrapper: {
    align: 'center',
    '@media (min-width: 768px)': {
      textAlign: 'right'
    }
  },
  cardRoot: {},
  content: {
    padding: 24,
    textAlign: 'center'
  },
  pageContainer: {
    justifyContent: 'space-around',
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'normal'
    }
  },
  selectedTradePartner: {
    textAlign: 'left',
    minWidth: '150px',
    maxWidth: '100%',
    marginBottom: theme.spacing(0.5)
  },
  ellipsis: {
    fontSize: 14,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible'
    }
  }
}));
